import { jsx as _jsx } from "react/jsx-runtime";
import { ComputerMark } from '#pplx/frontend/aether/components/Logo/ComputerMark.js';
import { Full } from '#pplx/frontend/aether/components/Logo/Full.js';
import { Mark } from '#pplx/frontend/aether/components/Logo/Mark.js';
import { Word } from '#pplx/frontend/aether/components/Logo/Word.js';
import classNames from 'classnames';
import React, { useMemo } from 'react';
const ColorClassMap = {
    foreground: 'text-primary',
    quiet: 'text-secondary',
    super: 'text-super-primary',
    caution: 'text-negative-primary',
    mixed: 'text-primary',
    white: 'text-light',
    background: 'text-inverse',
};
const MarkSizeMap = {
    tiny: 'w-4 md:w-6',
    small: '',
    md: 'w-10',
    'md-large': 'w-20',
    large: 'w-24',
    xl: 'w-36',
};
const DefaultMarkSize = 'w-6 md:w-8';
/**
 * Renders the Perplexity or Computer brand logo in multiple variants (full lockup, wordmark, and
 * logomark) with configurable size, color, and hover effects. Use the brand prop to switch between
 * Perplexity (default) and Computer identities. Supports product tier branding via isEnterprise and
 * isEducation flags on the text variant.
 *
 * @category components
 * @keywords logo, components, renders, perplexity, computer, brand, multiple, variants, full,
 *   lockup, wordmark, logomark, with, configurable, size, color, hover, effects, prop, switch,
 *   between, default, identities, supports, product, tier, branding, isenterprise, iseducation,
 *   flags, text, variant, icon, button, link
 * @whenToUse Use Logo to display the Perplexity or Computer brand identity across the application.
 *   Set brand='perplexity' (default) for the main product, or brand='computer' for the Computer
 *   sub-brand. Choose variant="full" for prominent placements like page headers and marketing hero
 *   sections, variant="mark" for compact brand presence in navigation bars and favicons, and
 *   variant="text" for inline wordmark displays. Use the isEnterprise or isEducation flags with the
 *   text variant to render product-specific wordmarks. When brand is 'computer', the variant
 *   defaults to 'mark' and renders the ComputerMark SVG.
 * @guidelineDo
 *   - Use the brand prop to select the product identity — 'perplexity' for the main Perplexity
 *     brand (default) and 'computer' for the Computer sub-brand
 *   - Use the variant prop to select the logo form — full for page headers, mark for compact spaces
 *     like favicons and nav bars, and text for inline wordmarks. When omitted, variant defaults
 *     based on brand ('computer' defaults to 'mark', 'perplexity' defaults to 'full')
 *   - Use the color prop with semantic tokens (foreground, quiet, super, caution, mixed, white,
 *     background) to keep the logo consistent with surrounding UI context and theme
 *   - Use the mixed color value when the logo stroke should use the brand accent color while the
 *     wordmark stays foreground
 *   - Set includeEffects to true on interactive surfaces where hover feedback on the logo is
 *     expected, such as homepage hero sections or clickable nav logos
 *   - Use the size prop to match the logo to its container — tiny or small for navigation bars,
 *     large or xl for hero sections and splash screens
 * @guidelineDont
 *   - Don't combine isEnterprise or isEducation flags with variants other than text — these flags
 *     only affect the wordmark rendering
 *   - Don't set includeEffects on static, non-interactive logo placements such as footers or print
 *     layouts
 *   - Don't use the white color token on light backgrounds or background token on dark backgrounds
 *     — the logo will be invisible against same-toned surfaces
 *   - Don't override the width of the wrapping div with custom classes — use the size prop to
 *     control dimensions so internal aspect ratios are preserved
 *   - Don't pass variant='full' with brand='computer' — the Computer brand has no full lockup and
 *     renders nothing; use the default mark, or variant='text' for the Computer wordmark
 * @accessibility
 *   - The Logo renders as a div containing an inline SVG — add a role="img" and an appropriate
 *     aria-label (e.g., "Perplexity" or "Computer") on the wrapper or a parent landmark when the
 *     logo conveys meaning
 *   - When the logo is wrapped in a link (e.g., a homepage link in a nav bar), the link's aria-label
 *     should describe the navigation target, not the logo itself
 *   - SVG elements use <use> references to sprite definitions — ensure the sprite sheet is loaded in
 *     the document for the logo to render
 *   - The includeEffects hover transitions are purely visual and do not affect keyboard or screen
 *     reader interaction
 * @relatedComponents icon, button, link
 */
export const Logo = React.memo(function Logo({ size = 'large', includeEffects = false, isPro = false, isMax = false, isEnterprise = false, isEducation = false, brand = 'perplexity', variant, color = 'foreground', className, style, ...divProps }) {
    const resolvedVariant = variant ?? (brand === 'computer' ? 'mark' : 'full');
    let fullLogoSize = 'w-28 md:w-[140px]';
    if (size === 'tiny') {
        fullLogoSize = 'w-14 md:w-16';
    }
    else if (size === 'small') {
        fullLogoSize = 'w-24';
    }
    else if (size === 'large') {
        fullLogoSize = 'w-40 md:w-52';
    }
    else if (size === 'xl') {
        // make enterprise/computer logos slightly bigger because they are much wider than the normal logos
        // this makes the line height feel more consistent
        fullLogoSize = isEnterprise || brand === 'computer' ? 'w-80' : 'w-64';
    }
    const markSize = MarkSizeMap?.[size] || DefaultMarkSize;
    const getLogoClassName = (width) => classNames(ColorClassMap[color], {
        'group-hover:text-super-primary transition-colors duration-300': includeEffects,
    }, className ?? ['h-auto group', width]);
    const logoStyle = color === 'mixed'
        ? { '--logo-stroke': 'var(--accent-border-strong)', ...style }
        : style;
    const wordVariant = useMemo(() => {
        if (brand === 'computer')
            return 'computer';
        if (isEducation)
            return 'education';
        if (isEnterprise && isPro)
            return 'enterprise-pro';
        if (isEnterprise && isMax)
            return 'enterprise-max';
        if (isEnterprise)
            return 'enterprise';
        if (isPro)
            return 'pro';
        if (isMax)
            return 'max';
        return 'perplexity';
    }, [brand, isEducation, isEnterprise, isPro, isMax]);
    if (brand === 'computer') {
        if (resolvedVariant === 'mark') {
            return (_jsx(ComputerMark, { className: getLogoClassName(markSize), style: logoStyle, ...divProps }));
        }
        if (resolvedVariant === 'text') {
            return (_jsx(Word, { className: getLogoClassName(fullLogoSize), style: logoStyle, variant: "computer", ...divProps }));
        }
        return null;
    }
    if (resolvedVariant === 'mark')
        return (_jsx("div", { className: classNames({
                'transition-all duration-300 ease-in-out hover:scale-105': includeEffects,
            }), children: _jsx(Mark, { className: getLogoClassName(markSize), style: logoStyle, ...divProps }) }));
    if (resolvedVariant === 'text')
        return (_jsx(Word, { className: getLogoClassName(fullLogoSize), style: logoStyle, variant: wordVariant, ...divProps }));
    if (resolvedVariant === 'full')
        return _jsx(Full, { className: getLogoClassName(fullLogoSize), style: logoStyle, ...divProps });
    return null;
});
//# sourceMappingURL=index.js.map